import React, { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import {
    CheckCircleFilled,
    CloseOutlined,
    FilePdfOutlined,
    PlusCircleOutlined,
} from "@ant-design/icons";
import { Button } from "../../../design-system";
import { ReactComponent as DocumentIcon } from "../../../assets/icons/svg/file-gradient.svg";
import type { QueuedDoc, QueuedQuestion } from "./types";
import classes from "./DrawerAssistant.module.less";

interface Props {
    queuedQuestions: QueuedQuestion[];
    queuedDocs: QueuedDoc[];
    submitted: boolean;
    onEditQuestion: (id: string, text: string) => void;
    onAddQuestion: (text: string) => void;
    onRemoveQuestion: (id: string) => void;
    onAttachDoc: (doc: QueuedDoc) => void;
    onRemoveDoc: (id: string) => void;
    onSubmit: () => void;
    onClose: () => void;
}

let docIdCounter = 0;

const SAMPLE_DOCS: Omit<QueuedDoc, "id">[] = [
    { filename: "Q1-2026-spend-forecast.xlsx", size: "184 KB" },
    { filename: "Azure-AWS-migration-cost-model.pdf", size: "412 KB" },
    { filename: "Marketplace-rollout-plan.docx", size: "67 KB" },
];

export const AskQueuePanel: React.FC<Props> = ({
    queuedQuestions,
    queuedDocs,
    submitted,
    onEditQuestion,
    onAddQuestion,
    onRemoveQuestion,
    onAttachDoc,
    onRemoveDoc,
    onSubmit,
    onClose,
}) => {
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const questionRefs = useRef<Map<string, HTMLTextAreaElement>>(new Map());
    const focusNextRef = useRef(false);

    const totalItems = queuedQuestions.length + queuedDocs.length;

    // After clicking "Add a question or note", focus the newly appended
    // textarea so the user can type immediately.
    useEffect(() => {
        if (!focusNextRef.current) return;
        const last = queuedQuestions[queuedQuestions.length - 1];
        if (!last) return;
        const el = questionRefs.current.get(last.id);
        if (el) {
            el.focus();
            focusNextRef.current = false;
        }
    }, [queuedQuestions]);

    const handleAddQuestion = () => {
        focusNextRef.current = true;
        onAddQuestion("");
    };

    if (submitted) {
        return (
            <div className={classes.submittedPanel}>
                <CheckCircleFilled className={classes.submittedIcon} />
                <h2>Queued for the next round</h2>
                <p>
                    The analyst has your follow-ups, notes, and additional documents. You'll
                    get a fresh report and updated playbook within one business day.
                </p>
                <div className={classes.submittedActions}>
                    <Button type="primary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    const attachSample = () => {
        const sample = SAMPLE_DOCS[queuedDocs.length % SAMPLE_DOCS.length];
        onAttachDoc({ id: `qd-${++docIdCounter}`, ...sample });
    };

    return (
        <div className={classes.queuePanel}>
            <div className={classes.queueBody}>
                <section className={classes.queueSection}>
                    <div className={classes.queueSectionHeader}>
                        <span className={classes.queueSectionLabel}>Documents</span>
                        <span className={classes.queueSectionCount}>
                            {queuedDocs.length}
                        </span>
                    </div>
                    {queuedDocs.length > 0 && (
                        <ul className={classes.docList}>
                            {queuedDocs.map((d) => (
                                <li key={d.id} className={classes.docListItem}>
                                    <FilePdfOutlined className={classes.docListIcon} />
                                    <div className={classes.docListInfo}>
                                        <div className={classes.docListName}>
                                            {d.filename}
                                        </div>
                                        <div className={classes.docListMeta}>{d.size}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className={classes.queueItemRemove}
                                        onClick={() => onRemoveDoc(d.id)}
                                        title="Remove"
                                    >
                                        <CloseOutlined />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div
                        className={clsx(classes.dropZone, dragOver && classes.dropZoneActive)}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            attachSample();
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <DocumentIcon className={classes.dropZoneIcon} />
                        <div className={classes.dropZoneText}>
                            Drop or click to add files
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: "none" }}
                            onChange={(e) => {
                                e.preventDefault();
                                attachSample();
                                if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                        />
                    </div>
                </section>

                <section className={classes.queueSection}>
                    <div className={classes.queueSectionHeader}>
                        <span className={classes.queueSectionLabel}>
                            Questions and updates
                        </span>
                        <span className={classes.queueSectionCount}>
                            {queuedQuestions.length}
                        </span>
                    </div>
                    {queuedQuestions.length > 0 && (
                        <ul className={classes.queueList}>
                            {queuedQuestions.map((q) => (
                                <li key={q.id} className={classes.queueItem}>
                                    <textarea
                                        ref={(el) => {
                                            if (el) {
                                                questionRefs.current.set(q.id, el);
                                            } else {
                                                questionRefs.current.delete(q.id);
                                            }
                                        }}
                                        className={classes.queueItemEdit}
                                        value={q.text}
                                        rows={2}
                                        placeholder="Add a question or update for the analyst…"
                                        onChange={(e) =>
                                            onEditQuestion(q.id, e.target.value)
                                        }
                                    />
                                    <button
                                        type="button"
                                        className={clsx(
                                            classes.queueItemRemove,
                                            classes.queueItemRemoveFloating
                                        )}
                                        onClick={() => onRemoveQuestion(q.id)}
                                        title="Remove from queue"
                                    >
                                        <CloseOutlined />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button
                        type="button"
                        className={classes.addItemButton}
                        onClick={handleAddQuestion}
                    >
                        <PlusCircleOutlined /> Add a question or update
                    </button>
                </section>

            </div>

            <div className={classes.queueFooter}>
                <Button
                    type="primary"
                    size="large"
                    block
                    disabled={totalItems === 0}
                    onClick={onSubmit}
                >
                    Submit
                </Button>
            </div>
        </div>
    );
};
